import { ReactNode } from 'react';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  AmexIcon,
  DinersClubIcon,
  DiscoverIcon,
  EditIcon,
  GeneralIcon,
  MasterCardIcon,
  PaymentSize,
  VisaIcon,
} from 'src/svgs/Viagogo';
import { BuyerWithSalesDto, Feature } from 'src/WebApiController';

import * as styles from './BuyerCard.css';

/**
 * Normalizes raw Adyen card brand strings (e.g. "visapurchasingcredit",
 * "mcdebit") into clean display labels (e.g. "VISA", "MC").
 * Returns null if the brand is unrecognized or empty.
 */
export function normalizeCardBrand(
  brand: string | null | undefined
): string | null {
  if (!brand) return null;
  const lower = brand.toLowerCase();
  if (lower.startsWith('visa')) return 'VISA';
  if (lower.startsWith('mc') || lower.startsWith('mastercard')) return 'MC';
  if (lower.startsWith('amex') || lower.startsWith('american')) return 'AMEX';
  if (lower.startsWith('discover')) return 'DISCOVER';
  if (lower.startsWith('diners')) return 'DINERS';
  return null;
}

export function getCardBrandIcon(brand: string | null | undefined): ReactNode {
  const normalized = brand?.toLowerCase();
  if (normalized?.startsWith('visa')) return <VisaIcon size={PaymentSize.s} />;
  if (normalized?.startsWith('mc') || normalized?.startsWith('mastercard'))
    return <MasterCardIcon size={PaymentSize.s} />;
  if (normalized?.startsWith('amex') || normalized?.startsWith('american'))
    return <AmexIcon size={PaymentSize.s} />;
  if (normalized?.startsWith('discover'))
    return <DiscoverIcon size={PaymentSize.s} />;
  if (normalized?.startsWith('diners'))
    return <DinersClubIcon size={PaymentSize.s} />;
  return <GeneralIcon size={PaymentSize.s} />;
}

type BuyerCardProps = {
  buyer: BuyerWithSalesDto;
  onEdit?: (buyer: BuyerWithSalesDto) => void;
};

export const BuyerCard = ({ buyer, onEdit }: BuyerCardProps) => {
  const hasCleanPaymentDisplay = useUserHasFeature(Feature.CleanPaymentDisplay);
  const {
    buyerName,
    buyerEmail,
    billingCity,
    billingState,
    cardBrand,
    cardSummary,
  } = buyer;

  const addressParts = [billingCity, billingState].filter(Boolean);

  const addressText = addressParts.join(' · ');

  return (
    <Stack
      alignItems="center"
      justifyContent="spaceBetween"
      gap="m"
      width="full"
      className={styles.card}
    >
      <Stack direction="column" flexWrap="grow" style={{ minWidth: 0 }}>
        {buyerName && <span className={styles.name}>{buyerName}</span>}
        <Stack alignItems="center" className={styles.metaRow}>
          <span className={styles.email}>{buyerEmail}</span>
          <span className={styles.separator}>|</span>
          {addressText ? (
            <span>{addressText}</span>
          ) : (
            <div className={styles.emptyDash} style={{ width: '60px' }} />
          )}
          <span className={styles.separator}>|</span>
          {cardSummary ? (
            <Stack alignItems="center" gap="s" flexShrink={false}>
              {getCardBrandIcon(cardBrand)}
              <span>
                {hasCleanPaymentDisplay
                  ? [normalizeCardBrand(cardBrand), cardSummary]
                      .filter(Boolean)
                      .join(' ')
                  : `••••${cardSummary}`}
              </span>
            </Stack>
          ) : (
            <div className={styles.emptyDash} style={{ width: '40px' }} />
          )}
        </Stack>
      </Stack>
      {onEdit && (
        <button
          type="button"
          className={styles.editButton}
          onClick={(e) => {
            e.stopPropagation();
            onEdit(buyer);
          }}
        >
          <EditIcon size={vars.iconSize.m} />
        </button>
      )}
    </Stack>
  );
};
